import { createFileRoute } from "@tanstack/react-router";
import { HardHat, Layers, BarChart3, Users, FileText, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-gestao-para-construcao";
const TITLE = "Software de Gestão para Construção Civil — Business Dite Ads";
const DESC =
  "Software de gestão para construção civil: obras, equipes, ponto, orçamentos, faturamento e folha em uma única plataforma. Teste 14 dias grátis.";

const faqs = [
  {
    q: "O que faz um software de gestão para construção?",
    a: "Centraliza obras, equipes, prazos, custos e documentos para o empreiteiro rodar cada projeto com lucro — sem depender de planilhas, WhatsApp e papel.",
  },
  {
    q: "Serve para empreiteiros pequenos e médios?",
    a: "Sim. O Business Dite Ads atende empresas de 3 a 200 funcionários — alvenaria, concreto, telhado, elétrica/hidráulica e construtoras gerais.",
  },
  {
    q: "Minha equipe consegue usar pelo celular?",
    a: "Sim. A equipe bate ponto com GPS por qualquer smartphone, sem instalar app. O escritório usa o painel completo no navegador.",
  },
  { q: "Tem fidelidade?", a: "Não. Planos mensais, 14 dias grátis e você cancela quando quiser." },
];

export const Route = createFileRoute("/software-de-gestao-para-construcao")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-management-software",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-gestion-de-construccion",
      },
      { rel: "alternate", hrefLang: "pt", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-management-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "pt-BR",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Software de Gestão para Construção"
      h1="Gerencie obras, equipes e dinheiro em uma única plataforma"
      subhead="O Business Dite Ads reúne obras, ponto, orçamentos, faturamento e folha — feito para construtoras que querem lucro por obra, não só papelada."
      primaryCtaLabel="Começar teste grátis de 14 dias"
      secondaryCtaLabel="Ver preços"
      benefits={[
        "Sem cartão de crédito",
        "Configuração em até 1 hora",
        "Onboarding gratuito",
        "Cancele quando quiser",
      ]}
      featuresHeading="Tudo que um empreiteiro precisa em um painel"
      features={[
        {
          icon: HardHat,
          title: "Obras e projetos",
          description:
            "Planeje obras, atribua equipes, acompanhe o andamento e mantenha cada documento ligado ao projeto certo.",
        },
        {
          icon: Layers,
          title: "Do orçamento à fatura",
          description:
            "Transforme orçamentos com IA em contratos assinados e faturas imutáveis — sem redigitar nada.",
        },
        {
          icon: BarChart3,
          title: "Lucro por obra",
          description:
            "Veja custo real x receita por obra: mão de obra, materiais, despesas e margem atualizados ao vivo.",
        },
        {
          icon: Users,
          title: "Equipe e subempreiteiros",
          description:
            "Gerencie funcionários e subs, valores por hora ou diária e permissões por função.",
        },
        {
          icon: FileText,
          title: "Documentos e aprovações",
          description:
            "Aprovação de fatura pelo cliente, contratos assinados e recibos anexados a cada despesa.",
        },
        {
          icon: ShieldCheck,
          title: "Pronto para auditoria",
          description:
            "Histórico imutável, acesso por função e armazenamento seguro na nuvem para o fechamento fiscal.",
        },
      ]}
      howItWorksHeading="Do cadastro à primeira obra lucrativa em poucos dias"
      steps={[
        {
          title: "Importe obras e equipe",
          description:
            "Cadastre projetos ativos, funcionários e valores. Ajudamos você a sair das planilhas.",
        },
        {
          title: "Equipes batem ponto com GPS",
          description:
            "Os trabalhadores batem ponto pelo celular. O escritório vê horas, locais e custos em tempo real.",
        },
        {
          title: "Fature, pague e veja o lucro",
          description:
            "Envie faturas, rode a folha e veja a margem por obra atualizando automaticamente.",
        },
      ]}
      socialProofHeading="Por que construtoras escolhem o Business Dite Ads"
      socialProof={[
        "Substitua 4–5 ferramentas desconectadas por uma só plataforma",
        "Acabe com horas perdidas em planilhas e ponto manual",
        "Identifique obras não lucrativas antes que custem caro",
        "Pague a equipe certinho — por hora ou por diária",
        "Receba mais rápido com faturas aprovadas pelo cliente",
        "Disponível em português, espanhol e inglês",
      ]}
      faqHeading="Perguntas frequentes"
      faqs={faqs}
      finalCtaHeading="Pronto para gerenciar sua construtora como profissional?"
      finalCtaSubhead="Junte-se a empreiteiros que trocaram papel, planilhas e 5 apps pelo Business Dite Ads."
    />
  );
}
